import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useTransform,
  type PanInfo,
} from 'framer-motion';
import {
  BookOpen,
  ChevronLeft,
  CheckCircle,
  SkipForward,
  RotateCcw,
  Home,
  Star,
} from 'lucide-react';
import { useAppStore } from '../store/appStore';
import { useContentAccess } from '../hooks/useContentAccess';
import { getScenarioById } from '../data/scenarios';
import { getCategoryById } from '../data/categories';
import GuestGate from '../components/GuestGate';
import PremiumGate from '../components/PremiumGate';
import type { ScenarioCard } from '../types';

/* ── Swipeable practice card ─────────────────────────────────────── */
interface PracticeCardProps {
  scenario: ScenarioCard;
  onSwipeRight: () => void;
  onSwipeLeft: () => void;
  index: number;
}

function PracticeCard({
  scenario,
  onSwipeRight,
  onSwipeLeft,
  index,
}: PracticeCardProps) {
  const [revealed, setRevealed] = useState(false);
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-12, 12]);
  const opacity = useTransform(x, [-200, -100, 0, 100, 200], [0, 1, 1, 1, 0]);

  const rightIndicatorOpacity = useTransform(x, [0, 80, 160], [0, 0.8, 1]);
  const leftIndicatorOpacity = useTransform(x, [-160, -80, 0], [1, 0.8, 0]);

  const category = getCategoryById(scenario.categoryId);

  const handleDragEnd = useCallback(
    (_: unknown, info: PanInfo) => {
      const threshold = 100;
      if (info.offset.x > threshold) {
        onSwipeRight();
      } else if (info.offset.x < -threshold) {
        onSwipeLeft();
      }
    },
    [onSwipeRight, onSwipeLeft]
  );

  return (
    <div className="relative flex items-center justify-center w-full">
      {/* Swipe indicators */}
      <motion.div
        style={{ opacity: rightIndicatorOpacity }}
        className="absolute left-4 top-8 z-10 bg-green-500 text-white px-4 py-2 rounded-2xl font-bold text-sm rotate-[-12deg] pointer-events-none"
      >
        ✓ Practised!
      </motion.div>
      <motion.div
        style={{ opacity: leftIndicatorOpacity }}
        className="absolute right-4 top-8 z-10 bg-gray-400 text-white px-4 py-2 rounded-2xl font-bold text-sm rotate-[12deg] pointer-events-none"
      >
        Skip →
      </motion.div>

      <motion.div
        key={index}
        style={{ x, rotate, opacity }}
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.9}
        onDragEnd={handleDragEnd}
        initial={{ scale: 0.92, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.85 }}
        transition={{ type: 'spring', stiffness: 260, damping: 26 }}
        className="w-full cursor-grab active:cursor-grabbing"
      >
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden select-none">
          {/* Card header */}
          <div
            className={`bg-gradient-to-br ${category?.color ?? 'from-primary-500 to-violet-600'} px-5 pt-5 pb-4`}
          >
            <span className="text-3xl">{category?.icon}</span>
            <h2 className="font-display text-xl font-extrabold text-white mt-2 leading-tight">
              {scenario.title}
            </h2>
            <span className="bg-white/20 text-white text-xs font-semibold px-2.5 py-1 rounded-full mt-2 inline-block">
              Ages {scenario.ageGroup}
            </span>
          </div>

          {/* Card body */}
          <div className="p-5 space-y-4">
            <p className="text-gray-600 text-sm leading-relaxed">
              {scenario.scenarioDescription}
            </p>

            <div className="bg-primary-50 rounded-2xl p-4">
              <p className="text-xs font-bold text-primary-600 uppercase tracking-wide mb-1.5">
                Ask your child
              </p>
              <p className="text-primary-800 text-sm font-medium leading-relaxed">
                {scenario.parentPrompt}
              </p>
            </div>

            {/* Reveal section */}
            {!revealed ? (
              <button
                onClick={() => setRevealed(true)}
                className="w-full bg-primary-600 text-white py-3.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-primary-700 transition-colors"
              >
                Tap to reveal answer
              </button>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-3"
              >
                <div className="bg-green-50 rounded-2xl p-4">
                  <p className="text-xs font-bold text-green-700 uppercase tracking-wide mb-1.5">
                    Answer
                  </p>
                  <p className="text-green-900 text-sm leading-relaxed">
                    {scenario.revealAnswer}
                  </p>
                </div>

                <div className="space-y-2">
                  {scenario.actionSteps.slice(0, 3).map((step) => (
                    <div key={step.step} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary-600 text-white font-bold text-xs flex items-center justify-center flex-shrink-0">
                        {step.step}
                      </div>
                      <p className="text-gray-700 text-xs pt-1">{step.text}</p>
                    </div>
                  ))}
                </div>

                <div className="bg-primary-700 rounded-2xl p-4 text-center">
                  <p className="text-white/70 text-xs uppercase tracking-widest mb-1">
                    Remember
                  </p>
                  <p className="font-display font-extrabold text-white text-base leading-snug">
                    "{scenario.memoryPhrase}"
                  </p>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/* ── Celebration screen ──────────────────────────────────────────── */
interface CelebrationProps {
  practicedCount: number;
  totalCount: number;
  onPracticeAgain: () => void;
  onGoHome: () => void;
}

function Celebration({
  practicedCount,
  totalCount,
  onPracticeAgain,
  onGoHome,
}: CelebrationProps) {
  const points = practicedCount * 10;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: 'spring', stiffness: 200 }}
      className="flex flex-col items-center justify-center min-h-screen px-6 text-center bg-gradient-to-br from-violet-50 to-purple-50"
    >
      <motion.div
        animate={{ rotate: [0, -10, 10, -10, 10, 0] }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="text-8xl mb-6"
      >
        🎉
      </motion.div>

      <h1 className="font-display text-3xl font-extrabold text-primary-800 mb-2">
        Session Complete!
      </h1>
      <p className="text-gray-500 text-base mb-6">
        You practised {practicedCount} out of {totalCount} scenarios
      </p>

      {/* Points earned */}
      <div className="bg-white rounded-3xl shadow-card px-8 py-5 mb-8 flex items-center gap-4">
        <Star size={28} className="text-warm-500" fill="currentColor" />
        <div className="text-left">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">
            Points Earned
          </p>
          <p className="font-display text-3xl font-extrabold text-primary-700">
            +{points}
          </p>
        </div>
      </div>

      <div className="space-y-3 w-full max-w-xs">
        <button
          onClick={onPracticeAgain}
          className="w-full bg-primary-600 text-white py-4 rounded-2xl font-bold text-base shadow-md hover:bg-primary-700 transition-colors flex items-center justify-center gap-2"
        >
          <RotateCcw size={18} />
          Practice Again
        </button>
        <button
          onClick={onGoHome}
          className="w-full bg-white text-gray-600 py-4 rounded-2xl font-bold text-base border border-gray-200 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
        >
          <Home size={18} />
          Go Home
        </button>
      </div>
    </motion.div>
  );
}

/* ── Main Practice component ─────────────────────────────────────── */
export default function Practice() {
  const navigate = useNavigate();
  const practiceSession = useAppStore((s) => s.practiceSession);
  const setPracticeSession = useAppStore((s) => s.setPracticeSession);
  const markScenarioPracticed = useAppStore((s) => s.markScenarioPracticed);
  const { isGuest, canPractise } = useContentAccess();

  // Gate: guests and free users cannot access Practice Mode
  if (isGuest) {
    return (
      <>
        <div className="flex flex-col items-center justify-center min-h-screen px-6 text-center bg-cream">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="font-display text-2xl font-bold text-gray-700 mb-2">Create a free account</h2>
          <p className="text-gray-400 text-sm mb-6">Sign up to access Practice Mode and 50+ scenarios.</p>
          <GuestGate visible onDismiss={() => navigate(-1)} />
        </div>
      </>
    );
  }

  if (!canPractise()) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-6 text-center bg-cream">
        <PremiumGate visible feature="practice" onDismiss={() => navigate(-1)} />
      </div>
    );
  }

  const [currentIndex, setCurrentIndex] = useState(
    practiceSession?.currentIndex ?? 0
  );
  const [practicedIds, setPracticedIds] = useState<string[]>(
    practiceSession?.practicedIds ?? []
  );
  const [done, setDone] = useState(false);
  const [cardKey, setCardKey] = useState(0);

  // No session
  if (!practiceSession) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-6 text-center bg-cream">
        <div className="text-6xl mb-5">📚</div>
        <h2 className="font-display text-2xl font-bold text-gray-700 mb-2">
          No active session
        </h2>
        <p className="text-gray-400 text-sm mb-8">
          Choose a bundle from the library to start practising
        </p>
        <button
          onClick={() => navigate('/library')}
          className="flex items-center gap-2 bg-primary-600 text-white px-6 py-3.5 rounded-2xl font-bold shadow-md hover:bg-primary-700 transition-colors"
        >
          <BookOpen size={18} />
          Choose a Bundle
        </button>
      </div>
    );
  }

  const scenarioIds = practiceSession.scenarioIds;
  const totalCount = scenarioIds.length;

  // Celebration screen
  if (done) {
    return (
      <Celebration
        practicedCount={practicedIds.length}
        totalCount={totalCount}
        onPracticeAgain={() => {
          setCurrentIndex(0);
          setPracticedIds([]);
          setDone(false);
          setCardKey((k) => k + 1);
        }}
        onGoHome={() => {
          setPracticeSession(null);
          navigate('/');
        }}
      />
    );
  }

  const currentScenarioId = scenarioIds[currentIndex];
  const currentScenario = currentScenarioId
    ? getScenarioById(currentScenarioId)
    : undefined;

  const advance = () => {
    const next = currentIndex + 1;
    if (next >= totalCount) {
      setDone(true);
    } else {
      setCurrentIndex(next);
      setCardKey((k) => k + 1);
      // sync session
      setPracticeSession({
        ...practiceSession,
        currentIndex: next,
        practicedIds,
      });
    }
  };

  const handleSwipeRight = () => {
    // Mark as practiced
    if (currentScenarioId) {
      markScenarioPracticed(currentScenarioId);
      setPracticedIds((prev) => [...prev, currentScenarioId]);
    }
    advance();
  };

  const handleSwipeLeft = () => {
    // Skip
    advance();
  };

  const progressPercent = ((currentIndex) / totalCount) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-50 flex flex-col">
      {/* Top bar */}
      <div className="flex items-center gap-4 px-4 pt-12 pb-4">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-500 hover:text-gray-700"
        >
          <ChevronLeft size={22} />
        </button>
        <div className="flex-1">
          <div className="h-2.5 bg-white rounded-full overflow-hidden shadow-inner">
            <motion.div
              className="h-full bg-gradient-to-r from-primary-500 to-violet-600 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>
        </div>
        <span className="text-sm font-bold text-gray-500 min-w-[48px] text-right">
          {currentIndex + 1} / {totalCount}
        </span>
      </div>

      {/* Card area */}
      <div className="flex-1 px-4 py-2 flex flex-col">
        <AnimatePresence mode="wait">
          {currentScenario ? (
            <PracticeCard
              key={cardKey}
              index={cardKey}
              scenario={currentScenario}
              onSwipeRight={handleSwipeRight}
              onSwipeLeft={handleSwipeLeft}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400">
              <p>Loading scenario…</p>
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* Swipe hint + manual buttons */}
      <div className="px-4 pb-8 pt-3 space-y-3">
        <p className="text-center text-xs text-gray-400">
          Swipe right to mark practised · Swipe left to skip
        </p>
        <div className="flex gap-3">
          <button
            onClick={handleSwipeLeft}
            className="flex-1 flex items-center justify-center gap-2 bg-white border border-gray-200 text-gray-500 py-3 rounded-2xl font-semibold text-sm hover:bg-gray-50 transition-colors"
          >
            <SkipForward size={16} />
            Skip
          </button>
          <button
            onClick={handleSwipeRight}
            className="flex-1 flex items-center justify-center gap-2 bg-green-500 text-white py-3 rounded-2xl font-semibold text-sm hover:bg-green-600 transition-colors shadow-md"
          >
            <CheckCircle size={16} />
            Practised
          </button>
        </div>

        {/* Navigation: prev */}
        {currentIndex > 0 && (
          <button
            onClick={() => {
              setCurrentIndex((i) => i - 1);
              setCardKey((k) => k + 1);
            }}
            className="w-full flex items-center justify-center gap-1.5 text-gray-400 text-xs font-semibold py-2"
          >
            <ChevronLeft size={14} /> Previous card
          </button>
        )}
      </div>
    </div>
  );
}
